import { useContext, useState } from "react";
import { hasAdminPermission } from "shared/admin-permissions/hasAdminPermission";
import { AdminUserPermissions } from "shared/admin-permissions/permissionNames";
import { isDefaultReportTableResults } from "shared/api/types/pagination";
import ToolTip from "shared/components/common/ToolTip";
import PillarForm from "shared/components/pillar-form/PillarForm";
import {
  hideAdminReportInformationForSession,
  useAdminReportInformationHiddenForSession,
} from "shared/components/pillar-table/custom-reports/useAdminReportInformationHiddenForSession";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import ReportAccessProblemsPanel from "shared/components/report-access/ReportAccessProblemsPanel";
import ReportAccessUsedByPanel from "shared/components/report-access/ReportAccessUsedByPanel";
import { useReportAccessSummary } from "shared/components/report-access/useReportAccessSummary";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { SocietySessionContext } from "shared/context/SocietySessionContext";

/**
 * Above every table backed by a custom report, for admins with the
 * report_information permission: which default report replaced the page's
 * own API (if one did), the report's access problems and everything that
 * uses it. Hide puts this one away until the page loads again; Hide all
 * for session puts every one away until the browser is refreshed (e.g. while
 * screen sharing).
 */
const AdminReportInformationContent = () => {
  const { customReportResponse, testidTable } = usePillarTableContext();
  const session = useCustomSession();
  const router = useCustomRouter();
  const [isHidden, setIsHidden] = useState(false);
  const isHiddenForSession = useAdminReportInformationHiddenForSession();
  const canSee = hasAdminPermission({
    session,
    permissions: [AdminUserPermissions.report_information],
  });
  const customReportId = customReportResponse?.customReportId;
  const { data: summary } = useReportAccessSummary({
    filter: { customReportId },
    enabled: canSee && !isHiddenForSession && customReportId !== undefined,
  });
  const isDefaultReport = isDefaultReportTableResults(customReportResponse);

  if (
    !canSee ||
    isHidden ||
    isHiddenForSession ||
    customReportId === undefined ||
    (!isDefaultReport && !summary)
  ) {
    return null;
  }

  const testid = `${testidTable}-admin-report-information`;

  return (
    <div className="mb-2 flex w-full flex-col gap-1" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {isDefaultReport && (
          <>
            <span data-testid={`${testid}-default-report`}>
              Default report in use:{" "}
              <span className="font-semibold">
                {customReportResponse.reportName ?? "Untitled report"} (#
                {customReportId})
              </span>
            </span>
            <PillarForm.Button
              label="Open report"
              className="button-small-neutral-filled"
              testid={`${testid}-open-report`}
              onClick={() =>
                router.push(
                  `/society/${session.societyId}/reports/${customReportId}`,
                )
              }
            />
          </>
        )}
        <PillarForm.Button
          label="Hide"
          className="button-small-neutral-filled"
          testid={`${testid}-hide`}
          onClick={() => setIsHidden(true)}
        />
        <ToolTip
          tooltipClassName="mt-0.25 py-1 px-2 bg-neutral-light text-xs rounded-md border border-neutral-mid-300"
          tooltip="Hides report information till the time your browser is refreshed."
        >
          <PillarForm.Button
            label="Hide all for session"
            className="button-small-neutral-filled"
            testid={`${testid}-hide-all`}
            onClick={hideAdminReportInformationForSession}
          />
        </ToolTip>
      </div>
      {summary && (
        <>
          <ReportAccessProblemsPanel
            problems={summary.problems}
            title="Report access problems"
            testid={`${testidTable}-report-access-problems`}
          />
          <ReportAccessUsedByPanel
            consumers={summary.consumers}
            testid={`${testidTable}-report-access-used-by`}
          />
        </>
      )}
    </div>
  );
};

/**
 * Renders nothing outside a society (e.g. /admin/users), because the report
 * access hooks and panels need the current society.
 */
const AdminReportInformation = () => {
  const society = useContext(SocietySessionContext);
  if (!society) {
    return null;
  }
  return <AdminReportInformationContent />;
};

export default AdminReportInformation;
